import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import showdown from 'showdown';
import TurndownService from 'turndown';

const converter = new showdown.Converter();
const turndownService = new TurndownService();
const EDITOR_MODULES = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['clean']
    ],
};
const READ_ONLY_MODULES = { toolbar: false };
const FORMATS = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
];

const RichTextEditor = ({ value, onChange, readOnly = false }) => {
    const [htmlContent, setHtmlContent] = useState('');
    const lastEmittedRef = useRef('');
    const wrapperRef = useRef(null);
    const modules = useMemo(() => (readOnly ? READ_ONLY_MODULES : EDITOR_MODULES), [readOnly]);

    useEffect(() => {
        const incomingValue = value || '';
        const nextHtml = incomingValue.trim().startsWith('<')
            ? incomingValue
            : converter.makeHtml(incomingValue);

        if (incomingValue !== lastEmittedRef.current) {
            setHtmlContent((prev) => (prev === nextHtml ? prev : nextHtml));
        }
    }, [value]);

    const handleChange = (content) => {
        setHtmlContent(content);
        const markdown = turndownService.turndown(content);
        lastEmittedRef.current = markdown;
        onChange(markdown);
    };

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        // React strict-mode/dev remounts can occasionally leave duplicate Quill toolbars.
        const toolbars = wrapper.querySelectorAll('.ql-toolbar');
        if (toolbars.length > 1) {
            for (let i = 1; i < toolbars.length; i += 1) {
                toolbars[i].remove();
            }
        }
    }, [htmlContent, readOnly]);

    return (
        <div className="rich-text-editor" ref={wrapperRef}>
            <ReactQuill
                theme="snow"
                value={htmlContent}
                onChange={handleChange}
                modules={modules}
                formats={FORMATS}
                readOnly={readOnly}
            />
        </div>
    );
};

export default RichTextEditor;
